import { genId } from 'Util/_HumanWritten/IdUtils';

type ParsedSegment = {
	id: string;
	text: string;
	tag: 'b' | 'i' | 'u' | 'a' | null; // Add 'a' for links
	href?: string; // Optional property for the link URL
};

/**
 * `[b]bold[/b]`, `[i]`, `[u]` and `[a=href]text[/a]`. A tag only counts as markup when it is closed -
 * a stray `[b]` renders as the literal characters, so anything reading this has to agree on that.
 *
 * <p>Shared by the parser and {@link stripNoticeMarkup} so the two cannot drift: search has to match
 * what the banner renders, not what was typed. Both use it through `String.replace`, which resets
 * `lastIndex`, so the `g` flag being stateful does not leak between them.</p>
 */
const NOTICE_MARKUP = /\[([biua])(?:=(.*?))?\](.*?)\[\/\1\]/g;

/**
 * The banner's text as it reads on screen, with the markup tags removed and the words they wrapped
 * kept.
 *
 * <p>Written for the banners list's search box. Matching the raw text meant a search could not span a
 * tag - "ferry cancelled" missed `Ferry [b]cancelled[/b]` - and `[b]` itself matched every bolded
 * banner.</p>
 *
 * <p>One pass, not a recursive one, because the parser does not nest either: in
 * `[b]a [i]b[/i][/b]` the inner tags are part of the outer tag's content and render literally. A
 * second pass here would strip characters the banner still shows.</p>
 */
export const stripNoticeMarkup = (text: string): string =>
	text ? text.replace(NOTICE_MARKUP, (_match, _tag, _href, content: string) => content) : '';

export const parseNoticeText = (text: string): ParsedSegment[] => {
	if (!text) return [];

	const result: ParsedSegment[] = [];
	let lastIndex = 0;

	// The callback now receives more arguments: match, tag, href, content
	text.replace(NOTICE_MARKUP, (match, tag, href, content, offset) => {
		// Add plain text before the match
		if (offset > lastIndex) {
			result.push({ id: genId(), text: text.slice(lastIndex, offset), tag: null });
		}

		// Add the formatted segment (with href if it exists)
		result.push({
			id: genId(),
			text: content,
			tag,
			href,
		});
		lastIndex = offset + match.length;
		return match;
	});

	// Add any remaining plain text
	if (lastIndex < text.length) {
		result.push({ id: genId(), text: text.slice(lastIndex), tag: null });
	}

	return result;
};
